import React, { Fragment, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { intlShape } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { path, pathOr } from 'ramda';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import routeConfiguration from '../../../../routeConfiguration';
import { CONGRATS_PROPOSAL_PAGE } from '../../../../constants/page';
import { createResourceLocatorString } from '../../../../util/routes';
import { propTypes } from '../../../../util/types';
import * as validators from '../../../../util/validators';
import {
  formatFormValuesAsBookingData,
  getInitialValuesFromSearch,
} from '../../../../util/booking';
import { maskContactDetails } from '../../../../util/contactDetails';
import { parse } from '../../../../util/urlHelpers';
import { sendEnquiry } from '../../../../ducks/listing.duck';
import { Divider, FieldTextInput, Form, PrimaryButton, UserCard } from '../../../../components';

import { handleTrackingTransaction } from '../../../ContactPage/ContactPage.helpers';
import css from './ContactForm.module.css';
import SaveStripePaymentMethod from '../../SaveStripePaymentMethod';
import { ensurePaymentMethodCard, ensureStripeCustomer } from '../../../../util/data';
import { verifyMessageForBypass } from '../../../../util/antiBypass';
import { getUserToken } from '../../../../ducks/user.duck';

const ContactForm = (props) => {
  const {
    className,
    author,
    currentUser,
    intl,
    listingId,
    onSendEnquiry,
    sendEnquiryError,
    sendEnquiryInProgress,
    bookingData,
    proposalPriceAsMoney,
    mustSetupStripePaymentMethod = false,
    hasValidatedSetupIntent,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const [bypassError, setBypassError] = useState(null);
  const [networkBypassError, setNetworkBypassError] = useState(false);
  const [isBypassCheckInProgress, setIsBypassCheckInProgress] = useState(false);
  const handleSubmit = (formValues) => {
    const routes = routeConfiguration();
    const message = path(['message'], formValues);
    const messageFormatted = maskContactDetails(message.trim(), intl);
    const totalParticipants =
      pathOr(0, ['quantity', 'adults'], bookingData) +
      pathOr(0, ['quantity', 'children'], bookingData);
    const providerId = path(['id', 'uuid'], author);

    setBypassError(null);
    setNetworkBypassError(false);
    setIsBypassCheckInProgress(true);

    const token = getUserToken();

    verifyMessageForBypass(null, () => ({}), {
      txId: null,
      messageRaw: message.trim(),
      token,
      dontAnalyseContent: false,
    })
      .then((result) => {
        if (result === false) {
          setNetworkBypassError(true);
          return;
        }

        onSendEnquiry(
          path(['uuid'], listingId),
          messageFormatted,
          bookingData,
          location.search,
          proposalPriceAsMoney
        )
          .then((txId) => {
            const { uuid } = txId;
            handleTrackingTransaction(
              uuid,
              currentUser,
              totalParticipants,
              bookingData.bookingDate,
              providerId
            );

            history.push(
              createResourceLocatorString(CONGRATS_PROPOSAL_PAGE, routes, { id: uuid }, {})
            );
          })
          .catch(() => {});
      })
      .catch((e) => {
        if (e?.name === 'bypassing' || e?.name === 'isEvent') {
          setBypassError(e);
        } else {
          setNetworkBypassError(true);
        }
      })
      .finally(() => {
        setIsBypassCheckInProgress(false);
      });
  };
  const authorDisplayName = path(['attributes', 'profile', 'displayName'], author);
  const searchParams = parse(location.search);
  const initialValues = getInitialValuesFromSearch(searchParams);
  const messageHeadingText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.messageHeading',
  });
  const messageFooterText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.messageFooter',
  });
  const messageInfoText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.messageInfo',
  });
  const messagePlaceholderText = intl.formatMessage(
    { id: 'CheckoutProposalPage.EnquiryPanel.messagePlaceholder' },
    { name: authorDisplayName }
  );
  const messageLabelText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.messageLabel',
  });
  const messageRequiredText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.messageRequired',
  });
  const messageRequired = validators.required(messageRequiredText);
  const submitText = intl.formatMessage({ id: 'CheckoutProposalPage.EnquiryPanel.submit' });
  const sendEnquiryErrorText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.sendEnquiryError',
  });
  const bypassCheckErrorText = intl.formatMessage({
    id: 'CheckoutProposalPage.EnquiryPanel.bypassCheckError',
  });
  const classes = classNames(css.root, className);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      render={(formRenderProps) => {
        const { handleSubmit } = formRenderProps;

        return (
          <Fragment>
            <Form className={classes} id="contactForm" onSubmit={handleSubmit}>
              <div className={css.messageSection}>
                <div className={css.infoWrapper}>
                  <span className={css.infoHeading}>{messageHeadingText}</span>
                  <span className={css.info}>{messageInfoText}</span>
                </div>
                <UserCard className={css.userCard} user={author} />
                <FieldTextInput
                  className={css.fieldMessage}
                  type="textarea"
                  id="message"
                  name="message"
                  label={messageLabelText}
                  placeholder={messagePlaceholderText}
                  validate={messageRequired}
                />
              </div>
              <Divider className={css.divider} />
              <span className={css.infoFooter}>{messageFooterText}</span>
              {mustSetupStripePaymentMethod ? (
                <div className={css.PaymentMethodsForm}>
                  <SaveStripePaymentMethod />
                </div>
              ) : null}
              {(!mustSetupStripePaymentMethod || hasValidatedSetupIntent) && (
                <div className={css.submitContainer}>
                  {bypassError && (
                    <div className={css.bypassingErrorPadding}>
                      <div className={css.bypassingErrorContainer}>
                        <div className={css.bypassTitle}>
                          {intl.formatMessage({ id: 'InboxPage.BypassWarningTitle' })}
                        </div>
                        <div className={css.bypassDescription}>
                          {intl.formatMessage({ id: 'InboxPage.BypassWarningDescription' })}
                        </div>
                      </div>
                    </div>
                  )}
                  {sendEnquiryError && <span className={css.error}>{sendEnquiryErrorText}</span>}
                  {networkBypassError && <span className={css.error}>{bypassCheckErrorText}</span>}
                  <PrimaryButton
                    className={css.submitButton}
                    disabled={
                      sendEnquiryInProgress ||
                      isBypassCheckInProgress ||
                      (mustSetupStripePaymentMethod && !hasValidatedSetupIntent)
                    }
                    inProgress={sendEnquiryInProgress || isBypassCheckInProgress}
                    type="submit"
                  >
                    {submitText}
                  </PrimaryButton>
                </div>
              )}
            </Form>
          </Fragment>
        );
      }}
    />
  );
};

ContactForm.defaultProps = {
  className: null,
  currentUser: null,
  sendEnquiryError: null,
  monthlyTimeSlots: null,
};

ContactForm.propTypes = {
  className: string,
  author: propTypes.user.isRequired,
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  listingId: object.isRequired,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onSendEnquiry: func.isRequired,
  publicData: object.isRequired,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
  bookingData: object,
};

const mapDispatchToProps = (dispatch) => ({
  onSendEnquiry: (listingId, message, bookingData, searchParams, proposalPrice) =>
    dispatch(sendEnquiry(listingId, message, bookingData, searchParams, proposalPrice)),
});

const mapStateToProps = (state, props) => {
  const { id, sendEnquiryInProgress, sendEnquiryError } = state.listing;
  const { currentUser } = state.user;
  const hasDefaultPaymentMethod =
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id;
  const hasSetupIntent = !!path(
    ['attributes', 'profile', 'privateData', 'clientSecretSetupIntent'],
    currentUser
  );

  return {
    currentUser,
    listingId: id,
    sendEnquiryInProgress,
    sendEnquiryError,
    hasValidatedSetupIntent: hasDefaultPaymentMethod && hasSetupIntent,
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(ContactForm);
